import { SPORTS, type Sport } from "./types.js";

/**
 * The sport this deployment serves.
 *
 * One app per sport: badminton, pickleball and table tennis each get their own build and
 * their own URL, all pointing at one Supabase project. The schema stays sport-aware
 * because the roster and the ratings are shared — you sign up once and an admin approves
 * you once, and your badminton rating has nothing to do with your pickleball rating. What
 * goes away is sport as something the player picks: there is nothing to pick.
 *
 * Set `VITE_SPORT` at build time. A build with the wrong value would write pickleball
 * results into badminton ratings, so vite.config.ts refuses to build without it and this
 * refuses to boot without it. Dev falls back to badminton so `npm run dev` just works.
 */
function resolve(): Sport {
  const configured = import.meta.env["VITE_SPORT"] as string | undefined;

  if (configured) {
    const known = SPORTS.find((sport) => sport.value === configured);
    if (known) return known.value;
    throw new Error(
      `VITE_SPORT is "${configured}", which is not one of: ${SPORTS.map((s) => s.value).join(", ")}`,
    );
  }

  if (import.meta.env.DEV) return "badminton";

  throw new Error("VITE_SPORT is not set. Every deployment serves exactly one sport.");
}

export const SPORT: Sport = resolve();

const META = SPORTS.find((sport) => sport.value === SPORT)!;

/** "Badminton". The sport's name, for eyebrows and labels. */
export const SPORT_LABEL = META.label;

/** "Prestige Hillside Gateway Badminton". The name of this app. */
export const APP_NAME = META.appName;

/** "PHG Badminton". Short name for PWA home screen. */
export const APP_SHORT_NAME = META.shortName;

/** Points a game is played to. The default format's target. */
export const TARGET_SCORE = META.target;

/** The formats this sport is played in, most common first. */
export const FORMATS = META.formats;

/**
 * This sport's app icon, for the screens that draw it.
 *
 * Resolved by vite.config.ts against `public/<sport>/`, so it is the same file the tab,
 * the home screen and the manifest use, and it falls back to the shared one for a sport
 * that has no art of its own. Not a literal, because a literal is what put a shuttlecock
 * on the table-tennis sign-in screen.
 *
 * Guarded because `define` is Vite's, and vitest runs this module without it -- the
 * substitution never happens and the bare identifier is a ReferenceError at import time,
 * which takes down every test that reaches this file rather than the one that reads the
 * icon. Same reason `resolve` above falls back to badminton: outside a build there is no
 * sport to be wrong about, and no icon to draw either.
 */
export const APP_ICON: string =
  typeof __APP_ICON__ === "undefined" ? "/app-icon.png" : __APP_ICON__;
